import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { forumSchema } from '../../schemas';
import {
  GET_FORUMS_FULFILLED,
  GET_FORUMS_PENDING,
  GET_FORUMS_REJECTED,
  GET_FORUM_GENDER_RATIO_FULFILLED,
  GET_FORUM_GENDER_RATIO_PENDING,
  GET_FORUM_GENDER_RATIO_REJECTED,
  GET_FORUM_HOT_POSTS_FULFILLED,
  GET_FORUM_HOT_POSTS_PENDING,
  GET_FORUM_HOT_POSTS_REJECTED,
  GET_FORUM_INFO_FULFILLED,
  GET_FORUM_INFO_PENDING,
  GET_FORUM_INFO_REJECTED,
  GET_FORUM_POSTS_FULFILLED,
  GET_FORUM_POSTS_PENDING,
  GET_FORUM_POSTS_REJECTED,
  GET_FORUM_SCHOOLS_FULFILLED,
  GET_FORUM_SCHOOLS_PENDING,
  GET_FORUM_SCHOOLS_REJECTED,
  GET_FORUM_SUBSCRIPTION_COUNTS_FULFILLED,
  GET_FORUM_SUBSCRIPTION_COUNTS_PENDING,
  GET_FORUM_SUBSCRIPTION_COUNTS_REJECTED,
  GET_FORUM_TOPICS_FULFILLED,
  GET_FORUM_TOPICS_PENDING,
  GET_FORUM_TOPICS_REJECTED,
  GET_FORUM_TRENDS_FULFILLED,
  GET_FORUM_TRENDS_PENDING,
  GET_FORUM_TRENDS_REJECTED,
} from '../../types';

// State interfaces
interface AllState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface InfoState {
  forum: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface ChartState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface GenderRatioState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface TrendsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface SubscriptionCountsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface TopicsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface SchoolsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface HotPostsState {
  forum: string | null;
  data: any[];
  sinceDaysAgo: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface PostsState {
  forum: string | null;
  data: any[];
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface ForumState {
  all: AllState;
  genderRatio: GenderRatioState;
  hotPosts: HotPostsState;
  info: InfoState;
  posts: PostsState;
  schools: SchoolsState;
  subscriptionCounts: SubscriptionCountsState;
  topics: TopicsState;
  trends: TrendsState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_FORUMS_PENDING | typeof GET_FORUM_INFO_PENDING |
        typeof GET_FORUM_TRENDS_PENDING | typeof GET_FORUM_TOPICS_PENDING |
        typeof GET_FORUM_SCHOOLS_PENDING | typeof GET_FORUM_GENDER_RATIO_PENDING |
        typeof GET_FORUM_SUBSCRIPTION_COUNTS_PENDING | typeof GET_FORUM_POSTS_PENDING |
        typeof GET_FORUM_HOT_POSTS_PENDING;
}

interface RejectedAction {
  type: typeof GET_FORUMS_REJECTED | typeof GET_FORUM_INFO_REJECTED |
        typeof GET_FORUM_TRENDS_REJECTED | typeof GET_FORUM_TOPICS_REJECTED |
        typeof GET_FORUM_SCHOOLS_REJECTED | typeof GET_FORUM_GENDER_RATIO_REJECTED |
        typeof GET_FORUM_SUBSCRIPTION_COUNTS_REJECTED | typeof GET_FORUM_POSTS_REJECTED |
        typeof GET_FORUM_HOT_POSTS_REJECTED;
  error: any;
}

interface GetForumsFulfilledAction {
  type: typeof GET_FORUMS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetForumInfoFulfilledAction {
  type: typeof GET_FORUM_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetForumDataFulfilledAction {
  type: typeof GET_FORUM_TRENDS_FULFILLED | typeof GET_FORUM_TOPICS_FULFILLED |
        typeof GET_FORUM_SCHOOLS_FULFILLED | typeof GET_FORUM_GENDER_RATIO_FULFILLED |
        typeof GET_FORUM_SUBSCRIPTION_COUNTS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetForumPostsFulfilledAction {
  type: typeof GET_FORUM_POSTS_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    forum: string;
    offset: number;
    limit: number;
  };
}

interface GetForumHotPostsFulfilledAction {
  type: typeof GET_FORUM_HOT_POSTS_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    forum: string;
    sinceDaysAgo: number;
    offset: number;
    limit: number;
  };
}

type AllAction = PendingAction | GetForumsFulfilledAction | RejectedAction;
type InfoAction = PendingAction | GetForumInfoFulfilledAction | RejectedAction;
type DataAction = PendingAction | GetForumDataFulfilledAction | RejectedAction;
type PostsAction = PendingAction | GetForumPostsFulfilledAction | RejectedAction;
type HotPostsAction = PendingAction | GetForumHotPostsFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  ALL: {
    data: [],
    isFetching: false,
    error: null,
  },
  INFO: {
    forum: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  CHART: {
    data: [],
    isFetching: false,
    error: null,
  },
  GENDER_RATIO: {
    data: [],
    isFetching: false,
    error: null,
  },
  TRENDS: {
    data: [],
    isFetching: false,
    error: null,
  },
  SUBSCRIPTION_COUNTS: {
    data: [],
    isFetching: false,
    error: null,
  },
  TOPICS: {
    data: [],
    isFetching: false,
    error: null,
  },
  SCHOOLS: {
    data: [],
    isFetching: false,
    error: null,
  },
  HOT_POSTS: {
    forum: null,
    data: [],
    sinceDaysAgo: 1,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  POSTS: {
    forum: null,
    data: [],
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
};

const all = (state: AllState = INITIAL_STATE.ALL, action: AllAction): AllState => {
  switch (action.type) {
    case GET_FORUMS_PENDING:
      return {
        ...INITIAL_STATE.ALL,
        isFetching: true,
      };
    case GET_FORUMS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUMS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_FORUM_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_FORUM_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;

      if (!data) {
        return INITIAL_STATE.INFO;
      }

      const {
        entities,
        result,
      } = normalize(data, forumSchema);

      return {
        entities: merge(state.entities, entities),
        forum: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const trends = (state: TrendsState = INITIAL_STATE.TRENDS, action: DataAction): TrendsState => {
  switch (action.type) {
    case GET_FORUM_TRENDS_PENDING:
      return {
        ...INITIAL_STATE.TRENDS,
        isFetching: true,
      };
    case GET_FORUM_TRENDS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_TRENDS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const topics = (state: TopicsState = INITIAL_STATE.TOPICS, action: DataAction): TopicsState => {
  switch (action.type) {
    case GET_FORUM_TOPICS_PENDING:
      return {
        ...INITIAL_STATE.TOPICS,
        isFetching: true,
      };
    case GET_FORUM_TOPICS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_TOPICS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const schools = (state: SchoolsState = INITIAL_STATE.SCHOOLS, action: DataAction): SchoolsState => {
  switch (action.type) {
    case GET_FORUM_SCHOOLS_PENDING:
      return {
        ...INITIAL_STATE.SCHOOLS,
        isFetching: true,
      };
    case GET_FORUM_SCHOOLS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_SCHOOLS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const genderRatio = (state: GenderRatioState = INITIAL_STATE.GENDER_RATIO, action: DataAction): GenderRatioState => {
  switch (action.type) {
    case GET_FORUM_GENDER_RATIO_PENDING:
      return {
        ...INITIAL_STATE.GENDER_RATIO,
        isFetching: true,
      };
    case GET_FORUM_GENDER_RATIO_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_GENDER_RATIO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const subscriptionCounts = (state: SubscriptionCountsState = INITIAL_STATE.SUBSCRIPTION_COUNTS, action: DataAction): SubscriptionCountsState => {
  switch (action.type) {
    case GET_FORUM_SUBSCRIPTION_COUNTS_PENDING:
      return {
        ...INITIAL_STATE.SUBSCRIPTION_COUNTS,
        isFetching: true,
      };
    case GET_FORUM_SUBSCRIPTION_COUNTS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_SUBSCRIPTION_COUNTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const posts = (state: PostsState = INITIAL_STATE.POSTS, action: PostsAction): PostsState => {
  switch (action.type) {
    case GET_FORUM_POSTS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_FORUM_POSTS_FULFILLED: {
      const {
        forum,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        forum,
        data,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const hotPosts = (state: HotPostsState = INITIAL_STATE.HOT_POSTS, action: HotPostsAction): HotPostsState => {
  switch (action.type) {
    case GET_FORUM_HOT_POSTS_PENDING:
      return {
        ...INITIAL_STATE.HOT_POSTS,
        isFetching: true,
      };
    case GET_FORUM_HOT_POSTS_FULFILLED: {
      const {
        forum,
        sinceDaysAgo,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        forum,
        data,
        sinceDaysAgo,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_FORUM_HOT_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<ForumState>({
  all,
  genderRatio,
  hotPosts,
  info,
  posts,
  schools,
  subscriptionCounts,
  topics,
  trends,
});